import React from 'react'
import {
  Skeleton,
  Typography,
  Space,
  Form,
  Input,
  InputNumber,
  Switch,
  Upload,
  Button,
  Divider,
  Row,
  Col,
  message
} from 'antd'
import { UploadOutlined, MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import * as deepObject from 'deep-object-diff'
import { FormComponent, BaseFormComponentProps } from '../../../BaseComponent'
import { ShelterAdoptionPathForm } from '../../ShelterSettings'
import Config from '../../../../config'
import IShelter from '../../../../interfaces/IShelter'
import IAdoptionPathMessages from '../../../../interfaces/IAdoptionPathMessages'
import {
  SearchRequest,
  ShelterSettingAdopterPersonalDocumentsSaveRequest,
  ShelterSettingAdoptionPathSaveRequest
} from '../../../../services/api/interfaces/ApiRequest'
import { LanguageConsumer } from '../../../../context/LanguageContext'
import IAdopterPersonalDocuments from '../../../../interfaces/IAdopterPersonalDocuments'

type ShelterGeneralProps = BaseFormComponentProps & {
  uuid: string
  data: IShelter
  shelterAdoptionPathSettings: IAdoptionPathMessages | null
  shelterAdopterPersonalDocumentSettings: IAdopterPersonalDocuments

  loading?: boolean
  contentLoading: boolean

  history: any
  error: any

  loadShelter: (args: SearchRequest) => void
  loadShelterSetting: (args: SearchRequest) => void

  dispatchSaveAdopterPersonalDocumentSetting: (args: ShelterSettingAdopterPersonalDocumentsSaveRequest) => Promise<any>
  dispatchSaveAdoptionPathSetting: (args: ShelterSettingAdoptionPathSaveRequest) => Promise<any>
  dispatchSave: Function
}

export default class ShelterGeneral extends FormComponent<ShelterGeneralProps> {

  static defaultProps = {
    loading: false,
    contentLoading: true
  }

  state = {
    loaded: false,
    saving: false,
    fileList: [] as Array<any>,
    contractActive: false,
    contract_remove: undefined
  }

  form: any

  componentDidMount() {
    const { uuid, loadShelterSetting } = this.props

    // Validate permissions
    this.validateAccessPermissions()

    if (loadShelterSetting) {
      loadShelterSetting({
        search: {
          organization_shelter: {
            has: {
              uuid: uuid
            }
          }
        }
      })
    }
  }

  componentDidUpdate(prevProps: ShelterGeneralProps, prevState) {
    const { uuid, data, error, loading, contentLoading } = this.props
    
    this.validateAccessPermissions()
    let newState = Object.assign({}, this.state)
    const wasSaving = !newState.saving && prevState.saving

    // After all of our requeriments has been loaded, we can load our entity fully
    if (!data && !loading && !contentLoading && !this.state.loaded && !this.state.saving) {
      if (uuid) {
        this.loadShelterData()
      }
    }

    // Load state data
    if (!this.state.loaded) {
      // We need some shelter data in our state :)
      if (data && !contentLoading) {
        const { uuid, contract, shelter_contract } = data
  
        if (contract && shelter_contract) {
          newState.fileList = [
            {
              uid: uuid,
              name: 'contract.pdf',
              status: 'done',
              url: `${Config.cdnBase}organization/contract/file/${shelter_contract.file}`
            }
          ]
        } else {
          newState.fileList = []
        }
        
        newState.contractActive = contract
        newState.loaded = true
      }
    } else {
      if (wasSaving) {
        // Something went wrong
        if (error) {
          message.error(`Could not save shelter informations. Error: ${error}`)
        } else {
          newState.loaded = false

          // Reload shelter data
          this.loadShelterData()
          message.success('Shelter informations saved!')
        }
      }
    }

    const isDiff = Object.keys(deepObject.diff(this.state, newState)).length > 0
    if (isDiff) {
      this.setState(newState)
    }
  }

  loadShelterData = async () => {
    const { uuid, loadShelter } = this.props

    if (uuid) {
      await loadShelter({
        search: {
          uuid: uuid,
          contract_options: {
            with: {
              deleted: false
            }
          },
          organization_question_shelters: {
            with: {
              deleted: false,
              kiosk_reason_id: null,
              question_shelter_species: {
                with: true
              }
            }
          },
          organization_branches: {
            with: {
              deleted: false
            }
          },
          shelter_contract: {
            with: true
          },
          country: {
            with: {
              default_language: {
                with: true
              }
            }
          }
        },
        options: {
          _force_ids: true
        }
      })
    }
  }

  handleContractSwitchChange = (checked: boolean) => {
    this.setState({
      contractActive: checked
    })
  }

  handleSave = async (values: any) => {
    const {
      uuid,
      dispatchSave,
      dispatchSaveAdopterPersonalDocumentSetting,
      dispatchSaveAdoptionPathSetting,
    } = this.props
    const { fileList, contract_remove } = this.state


    values = {
      ...values,
      contract_base64: '',
      organization_shelter_uuid: uuid
    }
    if(contract_remove !== undefined) {
      values.contract_remove = contract_remove
    }

    if (values.contract) {
      // Fix new contract options
      if (values.contract_options) {
        values.contract_options = values.contract_options.map(option => {
          return {
            ...option,
            uuid: option.uuid || null
          }
        })
      }
    }

    // Shelter settings from form value
    const adopterPersonalDocuments = values.shelter_setting?.adopterPersonalDocuments || null
    const sponsor = values.shelter_setting?.sponsor || null
    const adoption_path = values?.shelter_setting?.adoption_path || null

    let adopterPersonalDocumentSettings = {} as any
    let sponsorSetting = {} as any
    const adoptionPathSetting: ShelterSettingAdoptionPathSaveRequest = {
      shelter_uuid: uuid,
      type_key: 'ADOPTION_PATH_MESSAGE',
      value: {},
      translation: []
    }

    if (adopterPersonalDocuments) {
      Object.keys(adopterPersonalDocuments).forEach(key => {
        if (adopterPersonalDocuments[key] !== undefined) {
          adopterPersonalDocumentSettings[key] = adopterPersonalDocuments[key]
        }
      })
    }

    if (sponsor) {
      Object.keys(sponsor).forEach(key => {
        if (sponsor[key] !== undefined) {
          // Parse sponsor settings translation
          if (key === 'translation') {
            sponsorSetting[key] = values.shelter_setting.sponsor[key].map(p => {
              return {
                language_uuid: p.language,
                value: {
                  ...p.value,
                  enabled: values.shelter_setting.sponsor.enabled
                }
              }
            })
          } else {
            sponsorSetting[key] = sponsor[key]
          }
        }
      })
    }

    if (adoption_path) {
      const translations = {}

      Object.keys(adoption_path).forEach(key => {
        const type = key.toLocaleUpperCase()
        const adoptionPathValue = adoption_path[key]
        
        adoptionPathSetting.value = {
          ...adoptionPathSetting.value,
          [type]: adoptionPathValue.message
        }

        adoptionPathValue.translation?.map(t => {
          translations[t.language] = {
            ...translations[t.language],
            [type]: t.value
          }
        })
      })

      const translationObject = Object.keys(translations).map(t => ({
        language_uuid: t,
        value: translations[t]
      }))

      adoptionPathSetting.translation = translationObject
      adoptionPathSetting.value.translation = translationObject
    }

    const dispatchAll = () => {
      // We should save settings before
      if (Object.keys(adopterPersonalDocumentSettings).length) {
        this.setState({
          saving: true
        }, async () => {

            await dispatchSaveAdopterPersonalDocumentSetting({
              shelter_uuid: uuid,
              type_key: 'ADOPTER_PERSONAL_DOCUMENTS',
              value: adopterPersonalDocumentSettings
            })

          if (Object.keys(adoptionPathSetting).length) {
            await dispatchSaveAdoptionPathSetting(adoptionPathSetting)
          }

          // Now we can save the shelter
          await dispatchSave(values)

          this.setState({
            saving: false
          })
        })
      } else {
        // Flag saving
        this.setState({
          saving: true
        }, async () => {
          // Now we can save the shelter
          await dispatchSave(values)

          this.setState({
            saving: false
          })
        })
      }
    }

    // Convert uploaded file to base64
    if (fileList.length > 0 && fileList[0].uid !== uuid) {
      const reader = new FileReader()

      reader.onload = (e: any) => {
        values.contract_base64 = e.target.result
        
        dispatchAll()
      }

      reader.readAsDataURL(fileList[0].originFileObj)
    } else {
      dispatchAll()
    }
  }

  renderContractOptions = () => {
    return (
      <Form.List name="contract_options">
        {(fields, { add, remove }) => {
          return (
            <>
              {
                fields.map(field => (
                  <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="start">
                    <Form.Item
                      {...field}
                      label="Title"
                      name={[field.name, 'text']}
                      fieldKey={[field.fieldKey, 'text']}
                      rules={[
                        {
                          required: true,
                          message: 'Missing a title'
                        }
                      ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      label="Description"
                      name={[field.name, 'description']}
                      fieldKey={[field.fieldKey, 'description']}
                      rules={[
                        {
                          required: true,
                          message: 'Missing a description'
                        }
                      ]}
                    >
                      <Input.TextArea rows={3} cols={50} />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      label="Required"
                      name={[field.name, 'required']}
                      fieldKey={[field.fieldKey, 'required']}
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>

                    <MinusCircleOutlined onClick={() => remove(field.name)} style={{marginTop: 50}} />
                  </Space>
                ))
              }

              <Form.Item>
                <Button type="dashed" block onClick={() => add()}>
                  <PlusCircleOutlined /> Add contract opt-in
                </Button>
              </Form.Item>
            </>
          )
        }}
      </Form.List>
    )
  }

  render() {
    const { data,  shelterAdopterPersonalDocumentSettings, shelterAdoptionPathSettings } = this.props
    const { loaded, saving, contractActive, fileList } = this.state

    if (!loaded || (saving && !data)) {
      return <Skeleton />
    }

    console.log({fileList})

    const uploadProps = {
      accept: '.pdf',
      action: '',
      disabled: !contractActive,
      fileList,
      customRequest: () => true,
      beforeUpload: (file: any) => {
        if (file.type !== 'application/pdf') {
          message.error(`${file.name} is not a PDF`)
          return false
        }
        return true
      },
      onChange: (info: any) => {
        if(info.file.status ==="removed"){
          this.setState({fileList:[], contract_remove: true})
          return
        }

        this.setState({
          contract_remove: undefined,
          fileList: [{
            ...info.file,
            status: 'done'
          }]
        })
      }
    } as any

    const values = {
      ...data,
      interview_config: {
        best_day: { order: 5, visible: false, required: false },
        best_time: { order: 4, visible: false, required: false },
        ...data.interview_config
      },
      shelter_setting: {
        adopterPersonalDocuments: shelterAdopterPersonalDocumentSettings,
        adoption_path: {
          both: {
            message: shelterAdoptionPathSettings?.BOTH || '',
            translation: shelterAdoptionPathSettings?.
              translation?.
                filter(t => t.value.BOTH).map(t => ({
                  language: t.language_uuid,
                  value: t.value.BOTH
                })) || []
          },
          remote: {
            message: shelterAdoptionPathSettings?.REMOTE || '',
            translation: shelterAdoptionPathSettings?.
              translation?.
                filter(t => t.value.REMOTE).map(t => ({
                  language: t.language_uuid,
                  value: t.value.REMOTE
                })) || []
          },
          in_person: {
            message: shelterAdoptionPathSettings?.IN_PERSON || '',
            translation: shelterAdoptionPathSettings?.
              translation?.
                filter(t => t.value.IN_PERSON).map(t => ({
                  language: t.language_uuid,
                  value: t.value.IN_PERSON
                })) || []
          }
        }
      },
      pet_listing: {
        sort_by_applications: data.sort_by_applications,
        display_breed: data.display_breed,
        test_mode: data.test_mode
      },
      petco_love_lost: data.petco_love_lost,
      send_medical_records: data.send_medical_records,
    }
    console.log("GENERAL", values)

    console.log(uploadProps)

    return (
      <Form
        name="organization_shelter"
        initialValues={values}
        layout="vertical"
        ref={ref => {
          if (!this.form) {
            this.form = ref
            this.forceUpdate()
          }
        }}
        onFinish={this.handleSave}
      >
        <Row gutter={24}>
          <Col xs={24} sm={12} md={12}>
            <Typography.Title level={5}>Name</Typography.Title>

            <Form.Item
              label="Shelter name"
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Missing a name'
                }
              ]}
            >
              <Input />
            </Form.Item>


       
            <Divider />

            <LanguageConsumer>
              {({ fetching, languages, requestLanguageList }) => this.form ? (
                <ShelterAdoptionPathForm form={this.form} contentLoading={fetching} shelter={data} languages={languages} loadLanguages={requestLanguageList} />
              ) : null}
            </LanguageConsumer>

            <Divider />

            <Typography.Title level={5}>Adoption contract</Typography.Title>

            <Row gutter={24}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Contract Active"
                  name="contract"
                  valuePropName="checked"
                >
                  <Switch onChange={this.handleContractSwitchChange} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                {
                  contractActive ? (
                    <Upload {...uploadProps}>
                      <Button icon={<UploadOutlined />}>
                        Upload Contract
                      </Button>
                    </Upload>
                  ) : null
                }
              </Col>
            </Row>

            {contractActive && <Typography.Title level={5}>Adoption contract opt-ins</Typography.Title>}
            
            {
              contractActive ? this.renderContractOptions() : null
            }

            <Divider />

            <Typography.Title level={5}>Pet Listing</Typography.Title>

            <Row gutter={24}>
              <Col xs={24} sm={8} md={8}>
                <Form.Item
                  label="Sort By Applications"
                  name={['pet_listing', 'sort_by_applications']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8} md={8}>
                <Form.Item
                  label="Display Breed"
                  name={['pet_listing', 'display_breed']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8} md={8}>
                <Form.Item
                  label="Test Mode"
                  name={['pet_listing', 'test_mode']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Typography.Title level={5}>Petco Love Lost</Typography.Title>
            <Typography.Text>When turned on, adopters will be prompted to opt in to Petco Love Lost after completing adoption checkout. (This is exclusive to dog adoptions).
</Typography.Text>

            <Row gutter={24}>
              <Col xs={24} sm={8} md={8}>
                <Form.Item
                  label="Show modal"
                  name={['petco_love_lost']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Title level={5}>Include medical records in the adoption files</Typography.Title>
            <Typography.Text>When turned on, medical records will be included separately from the adoption contract in the adopter platform. (This is exclusive for Shelter Buddy integrated adoptions)</Typography.Text>

            <Row gutter={24}>
              <Col xs={24} sm={8} md={8}>
                <Form.Item
                  label="Include"
                  name={['send_medical_records']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24} sm={12} md={12}>
            <Typography.Title level={5}>Interview Form</Typography.Title>
            <Typography.Text>All these toggle configurations below are part of the Interview Form, the last step for adopters to submit an application/inquiry to adopt.</Typography.Text>
            <br/><br/>
            <Typography.Text strong>Emergency Contact</Typography.Text>
            <Row gutter={24}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Order"
                  name={['interview_config', 'emergency_contact', 'order']}
                  hidden
                >
                  <InputNumber min={1} />
                </Form.Item>

                <Form.Item
                  label="Visible"
                  name={['interview_config', 'emergency_contact', 'visible']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Required"
                  name={['interview_config', 'emergency_contact', 'required']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text strong>Personal Reference</Typography.Text>
            <Row gutter={24}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Order"
                  name={['interview_config', 'personal_reference', 'order']}
                  hidden
                >
                  <InputNumber min={1} />
                </Form.Item>

                <Form.Item
                  label="Visible"
                  name={['interview_config', 'personal_reference', 'visible']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Required"
                  name={['interview_config', 'personal_reference', 'required']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text strong>Co-Applicant</Typography.Text>
            <Row gutter={24}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Order"
                  name={['interview_config', 'co_applicant', 'order']}
                  hidden
                >
                  <InputNumber min={1} />
                </Form.Item>

                <Form.Item
                  label="Visible"
                  name={['interview_config', 'co_applicant', 'visible']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Required"
                  name={['interview_config', 'co_applicant', 'required']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text strong>Best Day</Typography.Text>
            <Row gutter={24}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Order"
                  name={['interview_config', 'best_day', 'order']}
                  hidden
                >
                  <InputNumber min={1} />
                </Form.Item>

                <Form.Item
                  label="Visible"
                  name={['interview_config', 'best_day', 'visible']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Required"
                  name={['interview_config', 'best_day', 'required']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text strong>Best Time</Typography.Text>
            <Row gutter={24}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Order"
                  name={['interview_config', 'best_time', 'order']}
                  hidden
                >
                  <InputNumber min={1} />
                </Form.Item>

                <Form.Item
                  label="Visible"
                  name={['interview_config', 'best_time', 'visible']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Required"
                  name={['interview_config', 'best_time', 'required']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Typography.Title level={5}>Adopter Personal Documents</Typography.Title>

            <Typography.Text strong>State</Typography.Text>
            <Row gutter={24}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Visible"
                  name={['shelter_setting', 'adopterPersonalDocuments', 'state', 'visible']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Required"
                  name={['shelter_setting', 'adopterPersonalDocuments', 'state', 'required']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text strong>Document</Typography.Text>
            <Row gutter={24}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Visible"
                  name={['shelter_setting', 'adopterPersonalDocuments', 'document', 'visible']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Required"
                  name={['shelter_setting', 'adopterPersonalDocuments', 'document', 'required']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text strong>State UUID</Typography.Text>
            <Row gutter={24}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Visible"
                  name={['shelter_setting', 'adopterPersonalDocuments', 'state_uuid', 'visible']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Required"
                  name={['shelter_setting', 'adopterPersonalDocuments', 'state_uuid', 'required']}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
        
        <Divider />

        <Row>
          <Col span={12} offset={6}>
            <Button type="primary" htmlType="submit" loading={saving} size="large" ref={(refs) => this.actionRefs.save = refs} block>
              Save
            </Button>
          </Col>
        </Row>
      </Form>
    )
  }
}
